import { useCallback } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Alert, setViewPort } from '@/actions/index'
import type { LngLatBoundsLike } from 'maplibre-gl'
import useMapInstance from '@/hooks/useMapInstance'

interface ZoomToBoundsOptions {
  /**
   * Padding around the bounds in pixels
   * @default 20
   */
  padding?: number

  /**
   * Optional callback function to execute when the transition completes
   */
  onComplete?: () => void

  /**
   * Minimum zoom level to use (prevents zooming too far in on small areas)
   */
  minZoom?: number

  /**
   * Maximum zoom level to use (prevents zooming too far out)
   */
  maxZoom?: number
}

type ViewportState = {
  latitude?: number
  longitude?: number
  zoom?: number
  bearing?: number
  pitch?: number
  width?: number
  height?: number
  [key: string]: unknown
}

/**
 * A hook that provides functionality to zoom the map to specified bounds
 * Uses MapLibre GL JS fitBounds for smooth animations
 *
 * @returns An object containing zoomToBounds function
 *
 * @example
 * ```tsx
 * const { zoomToBounds } = useZoomToBounds();
 *
 * // Zoom to bounds
 * const handleClick = () => {
 *   zoomToBounds([[-74.01, 40.70], [-73.99, 40.75]]);
 * };
 * ```
 */
const useZoomToBounds = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const store = useStore()
  const { map, isReady } = useMapInstance()

  /**
   * Push the camera the map settled on into Redux so Map.jsx's controlled
   * viewState matches the imperative fitBounds/easeTo. Without this,
   * react-map-gl's transformCameraUpdate keeps forcing the stale props on the
   * next zoom gesture and the camera snaps back (MAM-857). Reading the live
   * camera (instead of the requested destination) keeps Redux truthful even
   * when the animation is interrupted by another movement.
   */
  const syncSettledViewport = useCallback(
    (mapInstance: NonNullable<typeof map>) => {
      const center = mapInstance.getCenter()
      const current =
        (store.getState() as { viewport?: ViewportState }).viewport ?? {}
      dispatch(
        setViewPort({
          ...current,
          longitude: center.lng,
          latitude: center.lat,
          zoom: mapInstance.getZoom(),
          bearing: mapInstance.getBearing(),
          pitch: mapInstance.getPitch(),
        })
      )
    },
    [dispatch, store]
  )

  /**
   * Zooms the map to the specified bounds using MapLibre GL JS fitBounds
   * Optimized for React 18 - stable callback that doesn't cause re-renders
   */
  const zoomToBounds = useCallback(
    (
      bounds: [[number, number], [number, number]],
      options: ZoomToBoundsOptions = {}
    ) => {
      const { onComplete, minZoom = 2, maxZoom = 20, padding = 20 } = options

      if (!bounds) {
        console.warn('zoomToBounds: No bounds provided')
        onComplete?.()
        return
      }

      if (!isReady || !map) {
        console.warn('zoomToBounds: Map instance not available')
        onComplete?.()
        return
      }

      try {
        // Convert bounds to MapLibre LngLatBoundsLike format
        const lngLatBounds: LngLatBoundsLike = [
          [bounds[0][0], bounds[0][1]], // southwest
          [bounds[1][0], bounds[1][1]], // northeast
        ]

        const finish = () => {
          syncSettledViewport(map)
          // Defer onComplete so callers (e.g. teleport's Redux style commit)
          // run after the viewport sync has been dispatched.
          onComplete?.()
        }

        // Use MapLibre GL JS fitBounds with linear:true (easeTo) for smooth direct transition
        // Without linear:true, fitBounds uses flyTo which zooms out then back in (van Wijk algorithm)
        // linear:true replicates the old FlyToInterpolator behavior from react-map-gl v5
        map.fitBounds(lngLatBounds, {
          padding,
          maxZoom,
          minZoom,
          duration: 800,
          linear: true,
          essential: true,
        })

        // Queue after react-map-gl's own moveend → setViewPort so our sync wins
        // if that handler wrote a stale controlled viewState.
        // void: map.once's overload includes Promise when called without a listener.
        void map.once('moveend', () => {
          queueMicrotask(finish)
        })
      } catch (err: unknown) {
        console.error('Error in zoomToBounds:', err)

        // Fallback: ease to center point
        try {
          const centerLng = (bounds[0][0] + bounds[1][0]) / 2
          const centerLat = (bounds[0][1] + bounds[1][1]) / 2
          const fallbackZoom = 12

          map.easeTo({
            center: [centerLng, centerLat],
            zoom: fallbackZoom,
            duration: 800,
            essential: true,
          })

          void map.once('moveend', () => {
            queueMicrotask(() => {
              syncSettledViewport(map)
              onComplete?.()
            })
          })

          dispatch(
            Alert({
              error: t('errors.warningFitBounds'),
              severity: 'info',
            })
          )
        } catch {
          const message = err instanceof Error ? err.message : String(err)
          dispatch(
            Alert({
              error: `${t('errors.unableToZoomToBounds')}: ${message}`,
              severity: 'error',
            })
          )
          onComplete?.()
        }
      }
    },
    [map, isReady, dispatch, t, syncSettledViewport]
  )

  return {
    zoomToBounds,
  }
}

export default useZoomToBounds
